import { getCorrelationId, getUserId, setContextValue } from './browser-correlation-id';
import { LogCategory, LogLevel } from './types';
/**
 * Browser-specific base logger class that provides common functionality without Node.js dependencies
 */
export class BrowserBaseLogger {
    /**
     * Creates a new BrowserBaseLogger instance
     * @param options Logger configuration options
     */
    constructor(options) {
        this.transports = [];
        this.options = {
            retentionDays: 15,
            minLevel: LogLevel.INFO,
            enableSampling: false,
            samplingRate: 1.0,
            sensitiveFields: ['password', 'token', 'secret', 'creditCard', 'ssn'],
            ...options,
        };
    }
    /**
     * Adds a transport to the logger
     * @param transport The transport to add
     */
    addTransport(transport) {
        this.transports.push(transport);
    }
    /**
     * Sets the correlation ID for the current context
     * @param correlationId The correlation ID to set
     */
    setCorrelationId(correlationId) {
        setContextValue('correlationId', correlationId);
    }
    /**
     * Gets the correlation ID for the current context
     * @returns The correlation ID or a new one if not set
     */
    getCorrelationId() {
        return getCorrelationId();
    }
    /**
     * Sets the user ID for the current context
     * @param userId The user ID to set
     */
    setUserId(userId) {
        setContextValue('userId', userId);
    }
    /**
     * Gets the user ID for the current context
     * @returns The user ID or undefined if not set
     */
    getUserId() {
        return getUserId();
    }
    /**
     * Determines if a log entry should be logged based on level and sampling
     * @param level The log level
     * @returns True if the log entry should be logged, false otherwise
     */
    shouldLog(level) {
        // Define log levels in order of severity (most severe to least severe)
        const levelHierarchy = [LogLevel.ERROR, LogLevel.WARN, LogLevel.INFO, LogLevel.DEBUG, LogLevel.TRACE];
        const minLevelIndex = levelHierarchy.indexOf(this.options.minLevel || LogLevel.INFO);
        const currentLevelIndex = levelHierarchy.indexOf(level);
        // Only log if current level is equal or more severe than min level
        // Lower index = more severe, so current level index should be <= min level index
        if (currentLevelIndex > minLevelIndex) {
            return false;
        }
        // Apply sampling if enabled (except for ERROR level)
        if (this.options.enableSampling && level !== LogLevel.ERROR) {
            return Math.random() < (this.options.samplingRate || 1.0);
        }
        return true;
    }
    /**
     * Formats a log message with context into a structured log entry
     * @param level The log level
     * @param message The log message
     * @param context Additional context information
     * @returns A structured log entry
     */
    formatLogEntry(level, message, context) {
        const timestamp = new Date().toISOString();
        const correlationId = context?.correlationId || getCorrelationId();
        const userId = context?.userId || getUserId();
        const entry = {
            timestamp,
            level,
            message,
            correlationId,
            userId,
            category: context?.category,
            app: {
                name: this.options.appName,
                version: this.options.appVersion,
                environment: this.options.environment,
                component: context?.version?.component,
            },
        };
        // Add additional context properties, excluding those already added
        if (context) {
            const excludedKeys = ['correlationId', 'userId', 'category', 'version'];
            Object.entries(context).forEach(([key, value]) => {
                if (!excludedKeys.includes(key)) {
                    entry[key] = this.sanitizeValue(key, value);
                }
            });
        }
        return entry;
    }
    /**
     * Sanitizes a value to remove sensitive information
     * @param key The key of the value
     * @param value The value to sanitize
     * @returns The sanitized value
     */
    sanitizeValue(key, value) {
        const sensitiveFields = this.options.sensitiveFields || [];
        // Check if the key is in the sensitive fields list
        if (sensitiveFields.includes(key.toLowerCase())) {
            return '[REDACTED]';
        }
        // Recursively sanitize objects
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            const sanitized = {};
            Object.entries(value).forEach(([k, v]) => {
                sanitized[k] = this.sanitizeValue(k, v);
            });
            return sanitized;
        }
        // Return arrays and primitives as-is
        return value;
    }
    /**
     * Logs a message to all transports
     * @param level The log level
     * @param message The log message
     * @param context Additional context information
     */
    logToTransports(level, message, context) {
        if (!this.shouldLog(level)) {
            return;
        }
        const logEntry = this.formatLogEntry(level, message, context);
        for (const transport of this.transports) {
            try {
                transport.log(level, message, logEntry);
            }
            catch (error) {
                // If a transport fails, log to console as a fallback
                console.error(`[Logger] Transport error: ${error instanceof Error ? error.message : String(error)}`);
            }
        }
    }
    /**
     * Logs an error message
     * @param message The log message
     * @param context Additional context information
     */
    error(message, context) {
        this.logToTransports(LogLevel.ERROR, message, {
            category: LogCategory.ERROR,
            ...context,
        });
    }
    /**
     * Logs a warning message
     * @param message The log message
     * @param context Additional context information
     */
    warn(message, context) {
        this.logToTransports(LogLevel.WARN, message, context);
    }
    /**
     * Logs an info message
     * @param message The log message
     * @param context Additional context information
     */
    info(message, context) {
        this.logToTransports(LogLevel.INFO, message, context);
    }
    /**
     * Logs a debug message
     * @param message The log message
     * @param context Additional context information
     */
    debug(message, context) {
        this.logToTransports(LogLevel.DEBUG, message, context);
    }
    /**
     * Logs a trace message
     * @param message The log message
     * @param context Additional context information
     */
    trace(message, context) {
        this.logToTransports(LogLevel.TRACE, message, context);
    }
    /**
     * Logs an error object with stack trace
     * @param error The error object
     * @param message Optional message to include
     * @param context Additional context information
     */
    logError(error, message, context) {
        const errorMessage = message || error.message;
        this.error(errorMessage, {
            category: LogCategory.ERROR,
            error: {
                name: error.name,
                message: error.message,
                stack: error.stack,
            },
            ...context,
        });
    }
}
//# sourceMappingURL=browser-base-logger.js.map